import { AfterContentChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { Country } from 'src/app/shared/models/lookup-data.model';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { FormService } from 'src/app/shared/services/form.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ApplicationAgentDetails } from '../models/application-agent-details';
import { compareApplicationAgentDetails } from '../shared/utils/ModelUtils';
import { Action_ApplicationSectionSaved } from '../state/actions';

@Component({
  selector: 'online-app-application-agent-details',
  templateUrl: './application-agent-details.component.html',
  styleUrls: ['./application-agent-details.component.scss'],
  host: {
    class: 'application-section-component'
  },
})
export class ApplicationAgentDetailsComponent implements OnInit, AfterContentChecked, OnDestroy{

  agentDetailsForm: FormGroup

  applicationId: number;
  isSaveSuccessful = false;
  isScrollDone = false;
  isFormSubmitted = false;
  isSubmittedApplication = false;
  initialApplicationAgentDetails: ApplicationAgentDetails;

  countries: Country[]
  
  private subscriptionsToDispose: Subscription[] = [];

  constructor(fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<any>,
    private modalService: ModalService,
    private formService: FormService,
    private applicationService: ApplicationService,) { 
      this.activatedRoute.parent.params.subscribe(params => {
        this.applicationId = +params['id'];
      });

      this.agentDetailsForm = fb.group({
        givenName: [null, Validators.compose([Validators.required, Validators.maxLength(50)])],
        familyName: [null, Validators.compose([Validators.maxLength(50)])],
        phoneCountryCode: [null, Validators.required],
        phoneNumber: [null, Validators.compose([Validators.required/*, Validators.pattern()*/])],
        emailAddress: [null, Validators.compose([Validators.required, Validators.email])],
        additionalInformation: [null, Validators.compose([Validators.maxLength(400)])],
      });
    }

  ngOnInit() {
    setTimeout(() => {
      this.countries = [this.getDefaultCountry(), ...this.activatedRoute.snapshot.data['countries']];

      this.applicationService.getApplicationAgentDetails(this.applicationId)
        .subscribe((response: SingleResultApiResponse) => {
          if (response.Result) {
            this.updateFormValuesFromApiResult(response.Result);
            this.initialApplicationAgentDetails = this.getApplicationAgentDetailsDto();
          }
        }, err => {
          console.error(err);
        }, () => {

        });

      this.applicationService.getApplicationDetails(this.applicationId)
        .subscribe((response: SingleResultApiResponse) => {
          if (response.Result) {
            this.isSubmittedApplication = response.Result.ApplicationStatus === 'Submitted';
            if (this.isSubmittedApplication) {
              this.agentDetailsForm.disable();
            }
          }
        }, err => {
          console.error(err);
        });

    }, 4);
  }

  ngAfterContentChecked(): void {
    if (!this.isScrollDone) {
      this.isScrollDone = this.formService.scrollToFirstErrorField();
    }
  }

  ngOnDestroy() {
    this.subscriptionsToDispose.forEach(s => s.unsubscribe());
  }

  countryCodeDescription(country: Country) {
    if (!country || !country.Code) {
      return 'Please select';
    }

    return `${country.Description} (+${country.PhoneCode})`;
  }

  private updateFormValuesFromApiResult(agentDetails: ApplicationAgentDetails): void {

    //console.log(englishProficiencyDetails);
    this.agentDetailsForm.patchValue({
      givenName: agentDetails.GivenName,
      familyName: agentDetails.FamilyName,
      phoneCountryCode: agentDetails.PhoneCountryCode,
      phoneNumber: agentDetails.PhoneNumber,
      emailAddress: agentDetails.EmailAddress,
      additionalInformation: agentDetails.AdditionalInformation,
    });

    this.agentDetailsForm.updateValueAndValidity();
  }

  //#region  getters for individual form controls

  get givenName() : FormControl {
    return this.agentDetailsForm.get('givenName') as FormControl;
  }  
  get familyName() : FormControl {
    return this.agentDetailsForm.get('familyName') as FormControl;
  }  
  get phoneCountryCode() : FormControl {
    return this.agentDetailsForm.get('phoneCountryCode') as FormControl;
  }  
  get phoneNumber() : FormControl {
    return this.agentDetailsForm.get('phoneNumber') as FormControl;
  }  
  get emailAddress() : FormControl {
    return this.agentDetailsForm.get('emailAddress') as FormControl;
  }  
  get additionalInformation() : FormControl {
    return this.agentDetailsForm.get('additionalInformation') as FormControl;
  }

  givenNameUpdate(event: any) {
    if(this.agentDetailsForm.get('givenName').value){
      this.agentDetailsForm.get('givenName').setValue(this.titleCaseWord(this.agentDetailsForm.get('givenName').value));
      this.agentDetailsForm.updateValueAndValidity();
    }
  }

  familyNameUpdate(event: any) {
    if(this.agentDetailsForm.get('familyName').value){
      this.agentDetailsForm.get('familyName').setValue(this.agentDetailsForm.get('familyName').value.toUpperCase());
      this.agentDetailsForm.updateValueAndValidity();
    }
  }

  titleCaseWord(word: string) {
    if (!word) return word;
    return word[0].toUpperCase() + word.substr(1).toLowerCase();
  }
  //#endregion

  previous() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'summary']);
  }
  next() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'payment']);
  }

  saveAndContinue(): void {
    this.isFormSubmitted = true;
    
    if (this.agentDetailsForm.valid) {
      const agentDetails = this.getApplicationAgentDetailsDto();

      this.applicationService.updateApplicationAgentDetails(this.applicationId, agentDetails)
      .subscribe((response: SingleResultApiResponse) => {
        this.dispatchSectionSaveAction();
        this.isSaveSuccessful = true;
        this.next();
      }, (errorResponse: ErrorResponse) => {
        const { errors } = errorResponse;
        //console.log('errors: ', errors);
        // this.setApiValidationError(errors);
        this.agentDetailsForm.updateValueAndValidity();

        this.dispatchSectionSaveAction();
        this.initialApplicationAgentDetails = this.getApplicationAgentDetailsDto();
        this.isScrollDone = false;
        this.formService.scrollToFirstErrorField();
      });

    }    
  }

  private getApplicationAgentDetailsDto(): ApplicationAgentDetails {
    const dto = new ApplicationAgentDetails();

    dto.GivenName = this.givenName.value;
    dto.FamilyName = this.familyName.value;
    dto.PhoneCountryCode = this.phoneCountryCode.value;
    dto.PhoneNumber = this.phoneNumber.value;
    dto.EmailAddress = this.emailAddress.value;
    dto.AdditionalInformation = this.additionalInformation.value;

    return dto;
  }

  private getDefaultCountry(): Country {
    return {
      Id: '',
      Code: '',
      Description: 'Please select',
      Status: '',
      IsAgentMandatory: false,
      CanStudyAbroad: false,
      PhoneCode: 0,
      IsDeleted: false
    };
  }

  dispatchSectionSaveAction() {
    this.store.dispatch({
      type: Action_ApplicationSectionSaved
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaveSuccessful) {
      return true;
    }

    if (!this.isAgentDetailsSame()) {
      this.modalService.showModal();
      return this.modalService.stayOnThePage;
    } else {
      this.modalService.hideModal();
      return true;
    }
  }


  isAgentDetailsSame(): boolean {
    const currentAgentDetails = this.getApplicationAgentDetailsDto();
    const initialAgentDetails = this.initialApplicationAgentDetails;      

    return compareApplicationAgentDetails(initialAgentDetails, currentAgentDetails);;
  }

  //accessibility -- link error msg to form field
  errorDoubleErrorAccessibility(formControlName: string, requireErrorId: string, apiErrorId: string): string {
    if (this.isFormSubmitted &&
      this.agentDetailsForm.get(formControlName).errors &&
      this.agentDetailsForm.get(formControlName).errors.required) {
      return requireErrorId;
    } else if (this.isFormSubmitted &&
      this.agentDetailsForm.get(formControlName).errors &&
      this.agentDetailsForm.get(formControlName).errors.apiValidationError) {
      return apiErrorId;
    } else {
      return "";
    }
  }

}
